import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Music2, Guitar } from "lucide-react";
import api from "@/lib/api";
import { ArtistCard } from "@/pages/Artists";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";
import useTranslated from "@/hooks/useTranslated";

export default function GenreDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const descT = useTranslated(`genre-desc-${slug}`, data ? [data.genre.description] : []);

  useEffect(() => {
    setData(null);
    api.get(`/genres/${slug}`).then((r) => setData(r.data)).catch(console.error);
  }, [slug]);

  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { genre: g, artists } = data;

  return (
    <div data-testid={`genre-page-${slug}`}>
      <div className="relative h-64 md:h-72">
        <img src={g.image} alt={g.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <p className="font-script text-2xl text-pink-300 -rotate-1">{g.tagline}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white flex items-center gap-3 flex-wrap">{g.name} <PronounceButton text={g.name} /></h1>
          <p className="mt-2 text-slate-200 text-sm flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {g.origin_place} · <Link to={`/places/${g.country_slug}`} className="font-bold underline decoration-pink-400">{g.origin_country}</Link>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <AIContentBlock pageKey={`genre-${slug}`} topic={`${g.name} — the music tradition of ${g.origin_place}, ${g.origin_country}`} imgQuery={`${g.name} music performance`} />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <p className="text-slate-600 leading-relaxed" data-testid="genre-description">{descT[0] || g.description}</p>
            <h2 className="font-display text-2xl font-black text-slate-900 mt-8 mb-4 flex items-center gap-2"><Music2 className="w-5 h-5 text-purple-500" /> Musical Characteristics</h2>
            <AIContentBlock pageKey={`genre-${slug}-characteristics`} topic={`What ${g.name} sounds like — its rhythms, textures and defining musical characteristics`} compact />
            <div className="grid sm:grid-cols-2 gap-3" data-testid="genre-characteristics">
              {g.characteristics?.map((c) => (
                <div key={c} className="bg-white rounded-2xl border border-slate-100 p-4 text-sm font-bold text-slate-800">{c}</div>
              ))}
            </div>
          </div>
          <div>
            <div className="bg-white rounded-3xl border border-slate-100 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2"><Guitar className="w-4 h-4 text-emerald-600" /> Key Instruments</p>
              <div className="flex flex-wrap gap-2">
                {g.instruments?.map((i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">{i}</span>
                ))}
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-6 mb-2">Preservation status</p>
              <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold">{g.preservation_status}</span>
            </div>
          </div>
        </div>

        {artists.length > 0 && (
          <>
            <h2 className="font-display text-2xl font-black text-slate-900 mt-12 mb-5">Representative Artists</h2>
            <AIContentBlock pageKey={`genre-${slug}-artists`} topic={`The artists who carry the ${g.name} tradition forward today`} compact />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="genre-artists">
              {artists.map((a) => <ArtistCard key={a.slug} a={a} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
